import { createCreditCard } from '@api';
import { FormValues } from '@components/forms/CreditCardForm/CreditCardForm';
import {
  CardErrorsByField,
  emptyCardErrors,
} from '@components/forms/CreditCardForm/utils';
import { useApplicationStore } from '@data';
import { Events, tracking } from '@lib';
import { AddCardPaymentError } from '@qmd-types/errors';
import { type AdyenEncryptedMetadata } from '@quickmd-llc/design-system';
import { captureException } from '@sentry/react';
import { PatientCreditCard } from '@types';
import { isAxiosError } from 'axios';
import { getErrorMessage } from 'utils/misc/errorMessages';

interface AddCardProps {
  setCard?: (creditCard: PatientCreditCard) => void;
  closeAddCard?: () => void;
  setCardErrors: (errors: CardErrorsByField) => void;
  data: FormValues;
  adyenEncryptedMetadata: AdyenEncryptedMetadata;
}

export async function addCard(props: AddCardProps) {
  const { setCard, closeAddCard, setCardErrors, adyenEncryptedMetadata } = props;
  const { patient, setFraudData, fraudData, patientAccount } =
    useApplicationStore.getState();
  if (!patientAccount && !patient?.patientProfile) {
    alert('Patient account not found');
  }
  const patientAddress =
    patient?.patientProfile?.address || patientAccount?.address;
  try {
    const address = props.data.sameAsHomeAddress
      ? patientAddress!
      : props.data.address;
    const isDefault = Boolean(props.data.creditCard.isDefault);

    const newCreditCard = await createCreditCard({
      address,
      adyenEncryptedMetadata,
      isDefault,
    });

    patient.setPatientCreditCards([
      ...(patient.creditCards || []),
      newCreditCard,
    ]);
    if (setCard) {
      setCard(newCreditCard);
    }
    tracking.trackEvent(Events.SAVE_PAYMENT_METHOD);
    if (closeAddCard) closeAddCard();
    return newCreditCard.id;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      const errorMessage = getErrorMessage(error.name);
      setCardErrors({
        ...emptyCardErrors,
        general: [errorMessage],
      });
      if (error.name === 'Forbidden') {
        setFraudData({
          ...(fraudData || { toDateTime: '' }),
          remainingAttemptCount: 0,
        });
        if (closeAddCard) closeAddCard();
      }
      const responseData = error.response?.data;
      if (
        responseData &&
        (responseData as AddCardPaymentError).data?.attemptSummary
      ) {
        setFraudData(
          (responseData as AddCardPaymentError).data.attemptSummary!,
        );
      }
    } else {
      captureException(error);
      alert('An error occurred. Please try again.');
      console.error('Error adding credit card:', error);
    }
  }
}
